import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/dialog";
import { Camera, MapPin, MessageCircle, X, MoveRight } from "lucide-react";
import { motion } from "framer-motion";
import { COMPANY } from "@/lib/company";

const SHOWN_KEY = "fixtechnical_offer_popup_shown";

const WA_MESSAGE =
  "Hello Fix Technical Renovation Company, I would like a rough renovation estimate. I am sending photos/videos of my property now.";
const WA_LINK = `https://wa.me/${COMPANY.whatsappIntl.replace("+", "")}?text=${encodeURIComponent(WA_MESSAGE)}`;

const EASE = [0.16, 1, 0.3, 1];

const rise = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE },
});

const STEPS = [
  {
    n: "01",
    icon: Camera,
    from: "Photos & Videos",
    to: "Rough Estimate",
    note: "WhatsApp us your property — we price it remotely",
  },
  {
    n: "02",
    icon: MapPin,
    from: "Free Site Inspection",
    to: "Actual Price",
    note: "We visit, measure and confirm the final figure",
  },
];

export default function OfferPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(SHOWN_KEY)) return;
    const timer = setTimeout(() => {
      if (!sessionStorage.getItem(SHOWN_KEY)) {
        setOpen(true);
        sessionStorage.setItem(SHOWN_KEY, "1");
      }
    }, 18000);
    const exitHandler = (e) => {
      if (e.clientY < 10 && !sessionStorage.getItem(SHOWN_KEY)) {
        setOpen(true);
        sessionStorage.setItem(SHOWN_KEY, "1");
      }
    };
    document.addEventListener("mouseout", exitHandler);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseout", exitHandler);
    };
  }, []);

  const goToForm = () => {
    setOpen(false);
    setTimeout(() => {
      document.getElementById("lead-form")?.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="offer-popup"
        className="sm:max-w-[540px] w-[calc(100%-2rem)] rounded-none border-0 p-0 overflow-hidden gap-0 bg-neutral-950"
      >
        {/* Always-visible close button */}
        <DialogClose
          data-testid="popup-close"
          className="absolute top-3 right-3 z-50 w-10 h-10 rounded-full bg-white text-neutral-900 hover:bg-[#C5A059] hover:text-white flex items-center justify-center transition-colors duration-300 shadow-lg opacity-100 focus:outline-none focus:ring-2 focus:ring-[#C5A059]"
          aria-label="Close estimate popup"
        >
          <X className="w-4 h-4" />
        </DialogClose>

        {/* Gold gallery frame + glow + grain */}
        <div className="pointer-events-none absolute inset-2 border border-[#C5A059]/25 z-10" />
        <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-[#C5A059]/10 blur-[110px]" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="relative text-white px-7 py-10 sm:px-11 sm:py-12">
          {/* Eyebrow */}
          <motion.div
            {...rise(0.1)}
            className="inline-flex items-center gap-2.5 px-3 py-1.5 border border-[#C5A059]/50 text-[#E2C988] text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.28em] whitespace-nowrap mb-7"
          >
            <span className="relative flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#25D366] opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#25D366]" />
            </span>
            WhatsApp Estimate · Fast Reply
          </motion.div>

          {/* Kinetic masked headline */}
          <DialogTitle className="font-heading font-light leading-[1.02] tracking-tight mb-5">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block text-[26px] sm:text-4xl text-white"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.18, ease: EASE }}
              >
                Get Your Rough
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1.5">
              <motion.span
                className="block text-[26px] sm:text-4xl text-[#C5A059] italic"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
              >
                Renovation Estimate
              </motion.span>
            </span>
          </DialogTitle>

          <motion.div {...rise(0.45)}>
            <DialogDescription className="text-neutral-300 text-sm sm:text-[15px] leading-relaxed mb-8 max-w-md">
              Send us photos or videos of your property on WhatsApp and get a
              rough renovation estimate — before you commit to anything.
            </DialogDescription>
          </motion.div>

          {/* Numbered estimate chapters */}
          <div data-testid="estimate-steps" className="relative mb-8">
            <div className="absolute left-[27px] top-6 bottom-6 w-px bg-gradient-to-b from-[#C5A059]/50 via-[#C5A059]/20 to-transparent" />
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                {...rise(0.55 + i * 0.15)}
                data-testid={`estimate-step-${s.n}`}
                className="relative flex items-center gap-4 py-3 group"
              >
                <div className="relative shrink-0 w-14 h-14 border border-[#C5A059]/40 bg-neutral-900/80 flex items-center justify-center transition-colors duration-500 group-hover:border-[#C5A059]">
                  <s.icon className="w-5 h-5 text-[#C5A059]" strokeWidth={1.5} />
                  <span className="absolute -top-2 -left-2 bg-neutral-950 px-1 text-[9px] tracking-[0.2em] text-[#C5A059]">
                    {s.n}
                  </span>
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 text-sm sm:text-[15px]">
                    <span className="text-white font-medium">{s.from}</span>
                    <MoveRight className="w-4 h-4 text-[#C5A059] shrink-0" />
                    <span className="text-[#E2C988] font-medium">{s.to}</span>
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">{s.note}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            {...rise(0.85)}
            className="text-center text-[10px] uppercase tracking-[0.32em] text-neutral-400 mb-6"
          >
            No Guesswork · No Obligation
          </motion.p>

          {/* CTA */}
          <motion.div {...rise(0.95)}>
            <motion.a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="estimate-whatsapp-cta"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 22 }}
              className="group relative block w-full overflow-hidden bg-[#25D366] text-white text-center font-semibold text-sm sm:text-base py-5 px-6 shadow-[0_0_40px_rgba(37,211,102,0.25)]"
            >
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/25 to-transparent" />
              <span className="relative flex items-center justify-center gap-2.5">
                <MessageCircle className="w-5 h-5 shrink-0" />
                Send Your Property Photos &amp; Videos Now
              </span>
            </motion.a>
          </motion.div>

          <motion.button
            {...rise(1.05)}
            type="button"
            onClick={goToForm}
            data-testid="estimate-callback-link"
            className="mt-5 mx-auto block text-xs text-neutral-400 underline underline-offset-4 decoration-neutral-700 hover:text-[#C5A059] hover:decoration-[#C5A059] transition-colors duration-300"
          >
            Prefer a callback? Request a free site inspection
          </motion.button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
